import { useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  createAccountContact,
  saveAccountContact,
  deleteAccountContact,
} from "../../../../features/sg/usuarios";
import {
  notifyOk,
  notifyError,
  confirmAction,
} from "../../../../features/sg/notify";
import { getCustomSelectStyles } from "../../../../Layouts/admin/shared/theme/selectStyles";

interface AccountContactsProps {
  userId: string | number;
  initial: any[];
  contactTypes: any[];
  isDarkMode: boolean;
}

export default function AccountContacts({
  userId,
  initial,
  contactTypes,
  isDarkMode,
}: AccountContactsProps) {
  const { t } = useTranslation("sg");
  const [rows, setRows] = useState<any[]>(initial ?? []);
  const options = contactTypes.map((t) => ({ value: t.id, label: t.name }));

  const inputClass = isDarkMode
    ? "bg-[#232323] text-white border-dark-border"
    : "bg-white text-gray-900 border-[#DFE5EF]";

  async function addRow() {
    try {
      const row = await createAccountContact(userId);
      setRows((prev) => [...prev, row]);
    } catch (err: any) {
      notifyError(err.message);
    }
  }

  async function deleteRow(id: string | number) {
    const confirmed = await confirmAction({
      title: t("accountContacts.deleteTitle"),
      text: t("common.confirmText"),
      confirmText: t("accountContacts.deleteConfirm"),
    });
    if (!confirmed) return;
    try {
      await deleteAccountContact(id);
      setRows((prev) => prev.filter((x) => x.id !== id));
      notifyOk(t("accountContacts.deleted"));
    } catch (err: any) {
      notifyError(err.message);
    }
  }

  function patchRow(i: number, patch: any) {
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className={isDarkMode ? "text-white font-semibold" : "text-[#757575] font-semibold"}>
          {t("accountContacts.title")}
        </span>
        <button
          type="button"
          onClick={addRow}
          className="bg-primary-500 hover:bg-primary-400 text-white text-sm px-3 py-1.5 rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <i className="ri-add-line"></i> {t("accountContacts.add")}
        </button>
      </div>
      {rows.length === 0 ? (
        <p className={isDarkMode ? "text-gray-400 text-sm" : "text-gray-500 text-sm"}>
          {t("accountContacts.empty")}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((c, i) => (
            <div key={c.id} className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-3 items-center">
              <Select
                options={options}
                value={
                  options.find(
                    (o) => String(o.value) === String(c.contact_type_id)
                  ) ?? null
                }
                isClearable
                placeholder={t("accountContacts.typePlaceholder")}
                styles={getCustomSelectStyles(isDarkMode) as any} menuPortalTarget={document.body} menuPosition="fixed"
                onChange={(opt: any) => {
                  const contact_type_id = opt ? opt.value : null;
                  patchRow(i, { contact_type_id });
                  saveAccountContact({ ...rows[i], contact_type_id }).catch(
                    (err) => notifyError(err.message)
                  );
                }}
              />
              <input
                value={c.name ?? ""}
                placeholder={t("accountContacts.namePlaceholder")}
                onChange={(e) => patchRow(i, { name: e.target.value })}
                onBlur={() =>
                  saveAccountContact(rows[i]).catch((err) =>
                    notifyError(err.message)
                  )
                }
                className={`border-2 rounded-lg py-2 px-4 focus:outline-none hover:border-primary-500 focus:border-primary-500 transition-colors ${inputClass}`}
              />
              <button
                type="button"
                onClick={() => deleteRow(c.id)}
                className="bg-red-600 hover:bg-red-500 text-white text-sm px-3 py-2 rounded-lg transition-colors"
              >
                {t("accountContacts.delete")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
